import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { siteUrl } from "../../utils/constants";
import {
  ACCENT, categoryColor, categoryMarkStyle, GraphSelection,
} from "./graphShared";

/** Slide-in drawer showing the excerpts behind a graph selection — the
 *  same data (and the same copyright posture: snippets + capture/source
 *  links only) as the classic list view, scoped to the clicked term or
 *  term pair. */

interface Hit {
  id: number;
  url: string;
  crawl_date: string;
  snippet: string;
  captures: number;
}

interface ExcerptPage {
  total: number;
  captures: number;
  offset: number;
  pageSize: number;
  wayback: string;
  hits: Hit[];
}

const styles = `
/* In-flow drawer: it takes real width so the visualization reflows next
   to it instead of being covered. The inner wrapper keeps a fixed width
   so text doesn't reflow during the open/close animation. */
.evd-panel { position: relative; width: 0; flex: 0 0 auto;
  background: #fff; border-left: 0 solid #dee2e6; overflow: hidden;
  transition: width 300ms ease, border-left-width 300ms ease; }
.evd-panel.open { width: 400px; border-left-width: 1px; }
.evd-panel-inner { width: 400px; min-width: 400px; height: 100%;
  display: flex; flex-direction: column; }
/* Small screens: bottom sheet instead of side drawer. */
@media (max-width: 768px) {
  .evd-panel { width: 100%; height: 0; border-left: 0;
    border-top: 0 solid #dee2e6;
    transition: height 300ms ease, border-top-width 300ms ease; }
  .evd-panel.open { width: 100%; height: 55%; border-left-width: 0;
    border-top-width: 1px; }
  .evd-panel-inner { width: 100%; min-width: 0; }
}
/* Full mode: the excerpts take the whole stage as a multi-column reading
   view; the graph toggles back via the footer. */
.evd-panel.full.open { width: 100%; border-left-width: 0; }
@media (max-width: 768px) {
  .evd-panel.full.open { height: 100%; }
}
.evd-panel.full .evd-panel-inner { width: 100%; min-width: 0; }
.evd-panel.full .evd-panel-body { column-width: 380px;
  column-gap: 16px; }
.evd-panel.full .evd-hit { break-inside: avoid; }
.evd-panel-foot button { border: 0; background: none; padding: 0;
  color: #212529; text-decoration: underline; text-underline-offset: 2px;
  cursor: pointer; font-size: 13px; }
.evd-panel-head { padding: 18px 22px 12px; border-bottom: 1px solid #dee2e6; }
.evd-panel-head h5 { margin: 0 24px 4px 0; font-size: 17px; }
.evd-panel-head .dot { display: inline-block; width: 10px; height: 10px;
  border-radius: 50%; margin-right: 7px; }
.evd-panel-head .count { font-size: 13px; color: #757575; }
.evd-panel-close { position: absolute; top: 12px; right: 14px; border: 0;
  background: none; font-size: 22px; line-height: 1; color: #757575;
  cursor: pointer; }
.evd-panel-body { flex: 1; overflow-y: auto; padding: 16px 22px; }
.evd-panel .evd-hit { background: #fff; border: 1px solid #dee2e6;
  border-radius: 4px; padding: 14px 16px; margin-bottom: 14px; }
.evd-panel .evd-hit p.snippet { font-size: 14px; line-height: 1.6;
  color: #212529; margin-bottom: 8px; }
/* Highlights take the pinned term's category color (inline style);
   this rule is only the shape + a fallback fill. */
.evd-panel .evd-hit mark { background: ${ACCENT}; color: #fff;
  padding: 0 3px; border-radius: 3px; }
.evd-panel .evd-links { font-size: 12px; color: #757575;
  overflow-wrap: anywhere; }
.evd-panel .evd-links a { color: #212529; text-decoration: underline;
  text-underline-offset: 2px; }
.evd-panel-more { display: block; width: 100%; padding: 9px 0; border: 1px
  solid #dee2e6; background: none; color: #212529; cursor: pointer;
  border-radius: 4px; font-size: 14px; }
.evd-panel-more:hover { background: #f4f2ef; }
.evd-panel-foot { padding: 12px 22px; border-top: 1px solid #dee2e6;
  font-size: 13px; }
.evd-panel-foot a { color: #212529; text-decoration: underline;
  text-underline-offset: 2px; }
`;

/** Term matches arrive marked with \x01/\x02 sentinels; render them as
 *  <mark> without ever interpreting server text as HTML. markStyle tints
 *  the highlight with the pinned term's category color. */
const Snippet = ({ text, markStyle }: {
  text: string; markStyle?: React.CSSProperties;
}) => (
  <>
    {String(text).split("\x01").map((chunk, i) => {
      if (i === 0) return <React.Fragment key={i}>{chunk}</React.Fragment>;
      const [hit, ...rest] = chunk.split("\x02");
      return (
        <React.Fragment key={i}>
          <mark style={markStyle}>{hit}</mark>
          {rest.join("\x02")}
        </React.Fragment>
      );
    })}
  </>
);

interface ExcerptPanelProps {
  set: string;
  selection: GraphSelection | null;
  category?: number;
  onClose: () => void;
  /** Full mode: excerpts fill the stage instead of a side drawer. */
  full: boolean;
  onToggleFull: () => void;
  /** Display form of a term (proper nouns are title-cased). */
  labelOf: (term: string) => string;
  /** Include listings/directories (carried from the search lens). */
  listings?: boolean;
}

const ExcerptPanel = ({
  set, selection, category, onClose, full, onToggleFull, labelOf, listings,
}: ExcerptPanelProps) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<ExcerptPage | null>(null);
  const [hits, setHits] = useState<Hit[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const runSeq = useRef(0);

  useEffect(() => {
    setResult(null);
    setHits([]);
    setPage(0);
  }, [set, selection?.term, selection?.alsoTerm]);

  useEffect(() => {
    if (!set || !selection) return;
    const mySeq = runSeq.current + 1;
    runSeq.current = mySeq;
    setLoading(true);
    axios.get(`${siteUrl}/api/evidence/excerpts`, {
      params: {
        set, term: selection.term, alsoTerm: selection.alsoTerm || "", page,
        listings: listings ? 1 : 0,
      },
    }).then(({ data }: { data: ExcerptPage }) => {
      if (runSeq.current !== mySeq) return;
      setResult(data);
      setHits((prev) => (data.offset === 0 ? data.hits : [...prev, ...data.hits]));
      setLoading(false);
    }).catch(() => {
      if (runSeq.current === mySeq) setLoading(false);
    });
  }, [set, selection, page, listings]);

  const open = !!selection;
  const pair = selection?.alsoTerm;

  return (
    <>
      <style>{styles}</style>
      <aside
        className={`evd-panel${open ? " open" : ""}${full ? " full" : ""}`}
        aria-hidden={!open}
      >
        {selection && (
          <div className="evd-panel-inner">
            <div className="evd-panel-head">
              <button type="button" className="evd-panel-close" onClick={onClose}
                aria-label={t("evidence.close", "Close")}>×</button>
              <h5>
                {category !== undefined && (
                  <span className="dot"
                    style={{ background: categoryColor(category) }} />
                )}
                {labelOf(selection.term)}
                {pair && <> × {labelOf(pair)}</>}
              </h5>
              <span className="count">
                {result
                  ? `${result.total.toLocaleString()} ${t("evidence.pagesMention", "pages mention")} ${
                    pair ? t("evidence.bothTerms", "both terms") : `“${labelOf(selection.term)}”`
                  } (${result.captures.toLocaleString()} ${t("evidence.captures", "captures")})`
                  : t("evidence.loading", "loading…")}
              </span>
            </div>
            <div className="evd-panel-body">
              {hits.map((hit) => (
                <div key={`${hit.id}-${hit.url}`} className="evd-hit">
                  <p className="snippet">
                    <Snippet text={hit.snippet}
                      markStyle={category !== undefined
                        ? categoryMarkStyle(category) : undefined} />
                  </p>
                  <div className="evd-links">
                    <a href={`${result?.wayback}${hit.crawl_date || "*"}/${hit.url}`}
                      target="_blank" rel="noreferrer">
                      {t("evidence.capture", "original as crawled")}
                    </a>
                    {" · "}
                    <a href={hit.url} target="_blank" rel="noreferrer" title={hit.url}>
                      {t("evidence.source", "source page")}
                    </a>
                    {hit.captures > 1 && (
                      <span> · {hit.captures} {t("evidence.captures", "captures")}</span>
                    )}
                  </div>
                </div>
              ))}
              {result && hits.length < result.total && (
                <button type="button" className="evd-panel-more" disabled={loading}
                  onClick={() => setPage((p) => p + 1)}>
                  {loading
                    ? t("evidence.loading", "loading…")
                    : t("evidence.loadMore", "Load more")}
                </button>
              )}
            </div>
            <div className="evd-panel-foot">
              <button type="button" onClick={onToggleFull}>
                {full
                  ? `← ${t("evidence.backToGraph", "Back to graph view")}`
                  : `${t("evidence.expandExcerpts", "Expand excerpts to full view")} ⤢`}
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default ExcerptPanel;
